import { createContext, type ReactNode, useContext } from "react";
import type { RowData } from "@tanstack/react-table";
import { isDisplayColumnKind } from "../columns/columnLayout";
import type { DataGridResolvedColumnMeta, DataGridRow } from "../columns/types";

/** `true` inside the cells of a `parentRows: "summary"` parent row. */
export const SummaryRowContext = createContext(false);

/**
 * With `parentRows: "summary"`, whether the row is a parent: `isParentRow`'s
 * answer when it gives one, else whether the row has children in the data.
 * Read through `getSubRows`, not `row.subRows`, which search filters down:
 * a parent that matches with no matching children stays a summary row.
 */
export const getIsSummaryRow = <TData extends RowData>({
    hasParentSummaries,
    row,
    isParentRow,
}: {
    hasParentSummaries: boolean;
    row: DataGridRow<TData>;
    isParentRow: (row: TData) => boolean | undefined;
}) =>
    hasParentSummaries &&
    (isParentRow(row.original) ??
        (row.table.options.getSubRows?.(row.original, row.index)?.length ?? 0) >
            0);

/**
 * Whether a summary row shows this column: the tree's label, columns with
 * `summary`, and display columns, which keep rendering (checkbox, chevron,
 * number, actions, grip).
 */
export const isShownOnSummaryRow = (meta: DataGridResolvedColumnMeta) =>
    meta.tree || meta.hasSummary || isDisplayColumnKind(meta.kind);

interface SummaryCellProps {
    /** The column's `summary`, shown on summary rows. */
    summary: ReactNode;
    /** The column's cell, shown on every other row. */
    display: ReactNode;
}

/** A column with `summary`: the summary on summary rows, its cell elsewhere. */
export const SummaryCell = ({
    summary,
    display,
}: Readonly<SummaryCellProps>) =>
    useContext(SummaryRowContext) ? summary : display;
